import type { BrandMetaTemplateRegistry } from "./types";

/**
 * Per-brand metadata templates. Keyed by BrandId. Each brand's copy reflects
 * the audience psychology / positioning from its strategy.md + content-strategy
 * map. The brand NAME suffix is intentionally omitted — buildMetadata appends
 * `| <siteName>`.
 *
 * Claim-safety: no "fastest"/"guaranteed"/"no throttling"/"unlimited speed"/
 * fabricated metrics. eSIMCobra uses "quick"/"fast activation" framing.
 *
 * The default brand (esimsupra) is deliberately ABSENT so its metadata stays
 * byte-identical to the shared meta.json output.
 */
export const brandMetaTemplates: BrandMetaTemplateRegistry = {
  // -------------------------------------------------------------------------
  // 02 — eSIMCobra (bold-fast-activation): low-entry, fast checkout, brutalist.
  // "Strike online in 60 seconds." Quick activation, not "fastest".
  // -------------------------------------------------------------------------
  "bold-fast-activation": {
    home: {
      title: "Travel eSIM — Get Online in 60 Seconds",
      description:
        "Grab a travel eSIM and get online in about 60 seconds. Low-cost prepaid data for 180+ countries, quick QR activation, no SIM swap, no roaming bills.",
      ogTitle: "Travel eSIM — Strike Online in 60 Seconds",
      ogDescription:
        "Pick a plan, scan the QR, connect. Low-entry prepaid eSIM data for 180+ countries with fast activation and no roaming fees.",
      keywords:
        "cheap travel eSIM, quick eSIM activation, fast eSIM setup, prepaid eSIM, buy eSIM online, travel eSIM 60 seconds, low cost esim, international eSIM",
    },
    destinations: {
      title: "Travel eSIM Plans by Country — Quick Activation",
      description:
        "Pick your country and get a low-cost prepaid eSIM with quick QR activation. Data plans for Europe, USA, Japan, and 180+ destinations, no roaming fees.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Online in Minutes",
      description:
        "Land in {country} and get online fast. Low-cost prepaid eSIM data with quick QR activation, no SIM swap and no roaming charges.",
    },
    product: {
      title: "Travel eSIM Plan — Quick Activation",
      description:
        "Low-entry prepaid eSIM data plan with quick QR activation. Buy online, scan, and connect in minutes — no SIM swap, no roaming fees.",
    },
    howItWorks: {
      title: "How to Activate Your eSIM in About 60 Seconds",
      description:
        "Buy, scan the QR code, and get online in about 60 seconds. Step-by-step quick eSIM activation for iPhone, Samsung, and Pixel.",
    },
    compatibility: {
      title: "Is Your Phone eSIM Ready? Quick Compatibility Check",
      description:
        "Check whether your iPhone, Samsung, or Pixel supports eSIM in seconds, then activate a travel data plan with quick QR setup.",
    },
    support: {
      title: "eSIM Help & Support",
      description:
        "Need a hand with quick eSIM activation? Get straight answers on setup, QR codes, billing, and data so you can get back online fast.",
    },
  },

  // -------------------------------------------------------------------------
  // 03 — SkyLane (airport-fast-lane): arrival/airport/business/frequent flyer.
  // "Land connected, ready before arrival." Business-grade receipts.
  // -------------------------------------------------------------------------
  "airport-fast-lane": {
    home: {
      title: "Travel eSIM for Business Travelers — Land Connected",
      description:
        "Install before you board and land connected. Reliable travel eSIM data for frequent flyers and business travelers across 180+ countries, with clean receipts for expensing.",
      ogTitle: "Travel eSIM — Ready Before You Land",
      ogDescription:
        "Set up before departure, land with working data. Reliable prepaid eSIM for business travelers and frequent flyers, with expense-ready receipts.",
      keywords:
        "business travel eSIM, eSIM for frequent flyers, airport eSIM, travel eSIM for business, arrival eSIM, work travel data plan, eSIM receipt for expenses, international business eSIM",
    },
    destinations: {
      title: "Business Travel eSIM by Destination — Land Connected",
      description:
        "Choose a destination and arrive ready to work. Reliable prepaid eSIM data for business travelers across Europe, USA, Asia, and 180+ countries.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Land Connected for Business",
      description:
        "Arrive in {country} ready to work. Install before you board, land with reliable data, and keep a clean receipt for expensing — no roaming surprises.",
    },
    product: {
      title: "Business Travel eSIM Plan — Arrival-Ready Data",
      description:
        "Reliable prepaid eSIM data for business trips. Install before you fly, land connected, and get an expense-ready receipt — no SIM swap, no roaming.",
    },
    howItWorks: {
      title: "Set Up Your eSIM Before You Board",
      description:
        "Install before departure so you land connected. Step-by-step eSIM setup for iPhone, Samsung, and Pixel, built for business travel.",
    },
    compatibility: {
      title: "eSIM-Ready Devices for Business Travelers",
      description:
        "Confirm your work phone supports eSIM, then set up a reliable travel data plan that has you connected the moment you land.",
    },
    support: {
      title: "Business eSIM Support",
      description:
        "Travel support for business flyers: activation help, arrival-day setup, receipts for expensing, and answers on coverage and billing.",
    },
  },

  // -------------------------------------------------------------------------
  // 04 — AquaGlobe (aquaglass-global-connect): premium, smooth, beautifully
  // simple global connectivity. "Travel connected. Seamlessly."
  // -------------------------------------------------------------------------
  "aquaglass-global-connect": {
    home: {
      title: "Premium Travel eSIM — Seamless Global Connectivity",
      description:
        "Stay connected seamlessly across 180+ countries with a premium travel eSIM. Smooth setup, refined coverage, and prepaid data without roaming fees.",
      ogTitle: "Premium Travel eSIM — Beautifully Simple Connectivity",
      ogDescription:
        "Global data, made simple. A premium prepaid eSIM with smooth activation and seamless coverage across 180+ countries.",
      keywords:
        "premium travel eSIM, seamless global eSIM, smooth eSIM activation, global connectivity eSIM, prepaid eSIM, international eSIM, refined travel data plan",
    },
    destinations: {
      title: "Premium Travel eSIM by Destination — Seamless Coverage",
      description:
        "Choose your destination and stay connected seamlessly. Premium prepaid eSIM data with smooth setup for Europe, USA, Asia, and 180+ countries.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Seamless Premium Data",
      description:
        "Stay connected seamlessly in {country} with a premium prepaid eSIM. Smooth QR activation, refined coverage, and no roaming fees.",
    },
    product: {
      title: "Premium Travel eSIM Plan — Smooth & Seamless",
      description:
        "A premium prepaid eSIM data plan with smooth activation and seamless coverage. Scan, connect, and travel without roaming fees.",
    },
    howItWorks: {
      title: "How Your Premium eSIM Works — Smooth Setup",
      description:
        "Simple, refined eSIM setup in a few taps. Scan your QR code and connect seamlessly on iPhone, Samsung, or Pixel.",
    },
    compatibility: {
      title: "eSIM Compatibility — Is Your Device Ready?",
      description:
        "Check that your device supports eSIM, then enjoy seamless premium data with smooth activation across 180+ countries.",
    },
    support: {
      title: "Premium eSIM Support",
      description:
        "Refined, attentive support for your travel eSIM: smooth activation, coverage questions, billing, and seamless connectivity help.",
    },
  },

  // -------------------------------------------------------------------------
  // 05 — WanderLite (wanderlite-explorer): beginner/easy/first eSIM. Friendly.
  // "The friendly way to stay online while you travel."
  // -------------------------------------------------------------------------
  "wanderlite-explorer": {
    home: {
      title: "Your First Travel eSIM — Easy & Beginner-Friendly",
      description:
        "New to eSIMs? Get your first travel eSIM the easy way. Beginner-friendly prepaid data for 180+ countries with simple QR setup and no roaming fees.",
      ogTitle: "Travel eSIM Made Easy — Perfect for Beginners",
      ogDescription:
        "The friendly way to stay online while you travel. Simple, affordable prepaid eSIM data with easy step-by-step setup for first-timers.",
      keywords:
        "first eSIM, easy travel eSIM, beginner eSIM, how to use an eSIM, simple eSIM setup, affordable travel eSIM, eSIM for beginners, prepaid eSIM",
    },
    destinations: {
      title: "Easy Travel eSIM by Country — Beginner-Friendly Plans",
      description:
        "Pick your country and get online the easy way. Beginner-friendly prepaid eSIM data for Europe, USA, Japan, and 180+ destinations.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Easy Setup for Beginners",
      description:
        "Visiting {country}? Get your first eSIM the easy way. Beginner-friendly prepaid data, simple QR setup, and no confusing roaming charges.",
    },
    product: {
      title: "Easy Travel eSIM Plan — Beginner-Friendly",
      description:
        "An affordable, beginner-friendly prepaid eSIM data plan. Simple QR setup, clear steps, and no roaming fees — perfect for your first eSIM.",
    },
    howItWorks: {
      title: "How to Use Your First eSIM — Simple Step-by-Step",
      description:
        "New to eSIMs? Follow our friendly step-by-step guide to set up your first eSIM on iPhone, Samsung, or Pixel in minutes.",
    },
    compatibility: {
      title: "Does Your Phone Support eSIM? Easy Check",
      description:
        "Not sure if your phone works with eSIM? Do a quick, beginner-friendly compatibility check before you get your first travel data plan.",
    },
    support: {
      title: "Friendly eSIM Help for Beginners",
      description:
        "New to eSIMs and have questions? Get friendly, jargon-free help with setup, QR codes, and data so staying online feels easy.",
    },
  },

  // -------------------------------------------------------------------------
  // 06 — Travel Connected / photo-commerce: realistic trust, reviews,
  // photo-led commerce. "Travel connected in minutes."
  // -------------------------------------------------------------------------
  "photo-commerce-travel-connected": {
    home: {
      title: "Travel eSIM — Stay Connected in Minutes",
      description:
        "Buy a travel eSIM before you fly and stay connected in minutes. Real traveler reviews, clear coverage, and prepaid data for 180+ countries without roaming fees.",
      ogTitle: "Travel eSIM — Trusted by Travelers, Connected in Minutes",
      ogDescription:
        "Real reviews, honest coverage, and prepaid data for 180+ countries. Buy your travel eSIM before you fly and connect in minutes.",
      keywords:
        "travel eSIM reviews, trusted travel eSIM, prepaid eSIM data, buy eSIM before you fly, eSIM for travelers, international eSIM, travel connected eSIM",
    },
    destinations: {
      title: "Travel eSIM by Destination — Real Coverage, Real Reviews",
      description:
        "See coverage and traveler reviews by destination. Prepaid eSIM data for Europe, USA, Japan, and 180+ countries — connect in minutes, no roaming fees.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Connect in Minutes",
      description:
        "Heading to {country}? See real coverage and traveler reviews, then get a prepaid eSIM and connect in minutes — no SIM swap, no roaming fees.",
    },
    product: {
      title: "Travel eSIM Plan — Trusted, Connected in Minutes",
      description:
        "A prepaid eSIM data plan travelers trust. Clear coverage, honest details, and quick QR activation — connect in minutes without roaming fees.",
    },
    howItWorks: {
      title: "How Your Travel eSIM Works — Connect in Minutes",
      description:
        "See exactly how it works with clear, photo-led steps. Scan your QR code and connect in minutes on iPhone, Samsung, or Pixel.",
    },
    compatibility: {
      title: "eSIM Compatibility — Check Your Device",
      description:
        "Confirm your phone supports eSIM with a quick check, then choose a trusted prepaid plan and connect in minutes.",
    },
    support: {
      title: "Traveler eSIM Support",
      description:
        "Real help from a team that travels too: activation, QR codes, coverage, and billing answers so you stay connected with confidence.",
    },
  },

  // -------------------------------------------------------------------------
  // 07 — Re-digital (redigital-neon-dark-premium): app/control/data dashboard.
  // "Data control for the always-on traveler."
  // -------------------------------------------------------------------------
  "redigital-neon-dark-premium": {
    home: {
      title: "Travel eSIM with Full Data Control — Always On",
      description:
        "Take control of your travel data. Manage prepaid eSIM plans, monitor usage, and stay always-on across 180+ countries — no SIM swap, no roaming fees.",
      ogTitle: "Travel eSIM — Data Control for the Always-On Traveler",
      ogDescription:
        "Track usage, manage plans, and stay always-on. A travel eSIM with full data control and prepaid coverage across 180+ countries.",
      keywords:
        "travel eSIM data control, eSIM usage tracking, manage eSIM data, always-on travel eSIM, prepaid eSIM dashboard, digital nomad eSIM, international eSIM",
    },
    destinations: {
      title: "Travel eSIM by Destination — Manage Your Data",
      description:
        "Choose a destination and stay in control. Prepaid eSIM data with usage tracking for Europe, USA, Asia, and 180+ countries — always on.",
    },
    destinationDetail: {
      title: "{country} Travel eSIM — Data You Control",
      description:
        "Stay always-on in {country} with full data control. Prepaid eSIM data, usage tracking, and quick QR activation — no roaming fees.",
    },
    product: {
      title: "Travel eSIM Plan — Track & Control Your Data",
      description:
        "A prepaid eSIM data plan built for control. Monitor usage, manage your plan, and stay always-on with quick QR activation — no roaming fees.",
    },
    howItWorks: {
      title: "How It Works — Activate and Control Your Data",
      description:
        "Activate in a few taps, then track and manage your data. Step-by-step eSIM setup for iPhone, Samsung, and Pixel, built for control.",
    },
    compatibility: {
      title: "eSIM-Compatible Devices — Check Yours",
      description:
        "Confirm your device supports eSIM, then take full control of your travel data with usage tracking and always-on coverage.",
    },
    support: {
      title: "eSIM Support for the Always-On Traveler",
      description:
        "Technical support for digital travelers: activation, usage tracking, data management, and billing answers to keep you always-on.",
    },
  },

  // -------------------------------------------------------------------------
  // 08 — Coastal Freedom (coastal-freedom-travel-esim): vacation, high-data,
  // leisure. "Sunny, stress-free connectivity for your getaway."
  // -------------------------------------------------------------------------
  "coastal-freedom-travel-esim": {
    home: {
      title: "Vacation Travel eSIM — High-Data, Stress-Free",
      description:
        "Stay connected on your getaway without the stress. High-data travel eSIM plans for sharing photos and streaming across 180+ countries — no roaming fees.",
      ogTitle: "Travel eSIM for Your Getaway — Sunny & Stress-Free",
      ogDescription:
        "Relax and stay connected. High-data prepaid eSIM plans for vacations and leisure travel, with easy QR setup and no roaming bills.",
      keywords:
        "vacation travel eSIM, high data eSIM, holiday eSIM, leisure travel data plan, beach getaway eSIM, prepaid eSIM, streaming travel eSIM, international eSIM",
    },
    destinations: {
      title: "Vacation Travel eSIM by Destination — High-Data Plans",
      description:
        "Pick your getaway and stay connected stress-free. High-data prepaid eSIM plans for beaches, cities, and 180+ destinations — no roaming fees.",
    },
    destinationDetail: {
      title: "{country} Vacation eSIM — High-Data, Stress-Free",
      description:
        "Off to {country}? Share photos and stream on your getaway with a high-data prepaid eSIM. Easy QR setup and no roaming bills to spoil the trip.",
    },
    product: {
      title: "Vacation Travel eSIM Plan — High-Data Leisure",
      description:
        "A high-data prepaid eSIM plan made for getaways. Stream, share, and stay connected stress-free with easy QR setup and no roaming fees.",
    },
    howItWorks: {
      title: "How Your Vacation eSIM Works — Easy Setup",
      description:
        "Set up in minutes and relax. Scan your QR code and stay connected on your getaway with iPhone, Samsung, or Pixel.",
    },
    compatibility: {
      title: "Is Your Phone Ready for a Vacation eSIM?",
      description:
        "Quick compatibility check before your getaway: confirm your phone supports eSIM, then grab a high-data plan and travel stress-free.",
    },
    support: {
      title: "Vacation eSIM Support",
      description:
        "Easygoing help for your getaway: activation, QR setup, high-data plans, and billing answers so connectivity never stresses your trip.",
    },
  },

  // -------------------------------------------------------------------------
  // 09 — eSIM Index (esim-index-comparison-engine): comparison/ranking/
  // methodology. NOT a direct seller. "Compare travel eSIMs in seconds."
  // -------------------------------------------------------------------------
  "esim-index-comparison-engine": {
    home: {
      title: "Compare Travel eSIMs — Rankings & Methodology",
      description:
        "Compare travel eSIM providers side by side. Transparent rankings, a clear methodology, and coverage breakdowns for 180+ countries to help you choose.",
      ogTitle: "Travel eSIM Comparison — Rankings You Can Trust",
      ogDescription:
        "Independent travel eSIM comparisons with a transparent methodology. Compare providers, coverage, and value across 180+ destinations.",
      keywords:
        "travel eSIM comparison, compare eSIM providers, best travel eSIM, eSIM rankings, eSIM provider review, eSIM methodology, travel eSIM guide",
    },
    destinations: {
      title: "Travel eSIM Comparison by Country — Coverage & Value",
      description:
        "Compare travel eSIM options by destination. See coverage, value, and provider rankings for Europe, USA, Japan, and 180+ countries.",
    },
    destinationDetail: {
      title: "Best Travel eSIM for {country} — Compared & Ranked",
      description:
        "Compare the best travel eSIM options for {country}. Coverage, value, and provider rankings side by side, using a transparent methodology.",
    },
    howItWorks: {
      title: "Our eSIM Comparison Methodology — How We Rank",
      description:
        "How we compare and rank travel eSIMs: the criteria, data sources, and methodology behind our coverage and value assessments.",
    },
    compatibility: {
      title: "eSIM Device Compatibility — Comparison & Guide",
      description:
        "Check which devices support eSIM and compare how providers handle activation, then choose the right travel eSIM with confidence.",
    },
    support: {
      title: "eSIM Comparison Help & FAQ",
      description:
        "Questions about our travel eSIM rankings and methodology? Find answers on how we compare providers, coverage, and value.",
    },
  },

  // -------------------------------------------------------------------------
  // 10 — Voyage Journal (travel-destination-blog-affiliate-engine): travel
  // guides, itineraries, editorial, affiliate. "Guides, itineraries & staying
  // connected." NOT a direct storefront.
  // -------------------------------------------------------------------------
  "travel-destination-blog-affiliate-engine": {
    home: {
      title: "Travel Guides, Itineraries & Staying Connected",
      description:
        "Plan your next trip with destination guides, itineraries, and practical advice on staying connected abroad with a travel eSIM across 180+ countries.",
      ogTitle: "Travel Journal — Guides, Itineraries & Connectivity",
      ogDescription:
        "Editorial travel guides and itineraries plus honest advice on staying connected abroad. Plan smarter for 180+ destinations.",
      keywords:
        "travel guides, travel itineraries, destination guides, how to stay connected abroad, travel eSIM guide, trip planning, travel tips, where to stay",
    },
    destinations: {
      title: "Destination Guides — Plan Your Trip & Stay Connected",
      description:
        "Explore destination guides and itineraries for Europe, Asia, the Americas, and 180+ countries, with practical tips on staying connected abroad.",
    },
    destinationDetail: {
      title: "{country} Travel Guide — Itineraries & Connectivity Tips",
      description:
        "Plan your trip to {country} with our travel guide: itineraries, where to stay, things to do, and how to stay connected with a travel eSIM.",
    },
    howItWorks: {
      title: "Staying Connected Abroad — A Traveler's Guide",
      description:
        "How to stay connected on your trip: a practical guide to travel eSIMs, setup, and avoiding roaming fees, written for real travelers.",
    },
    compatibility: {
      title: "Travel eSIM Device Guide — What Works Abroad",
      description:
        "Which phones work with a travel eSIM and how to check before you go — part of our practical guide to staying connected abroad.",
    },
    support: {
      title: "Travel Help & FAQ",
      description:
        "Practical answers for trip planning and staying connected abroad: travel eSIM basics, itineraries, and where-to-stay guidance.",
    },
  },
};
